import { fmtDate, safeJson } from '../../../utils';
import type { ChangeRequest, Approver } from '../../../types';

interface Props {
  record: ChangeRequest;
  cid: string;
  onRefresh: () => Promise<void>;
}

export default function ApprovalSection({ record }: Props) {
  const approverList = safeJson<Approver[]>(record.approvers, []);
  const approverDisplay = approverList.map(a => a.name).join(', ');
  const isWalkthrough = record.status === 'Walkthrough Requested';
  const hasDecision =
    record.approvalDecision &&
    ['Approved', 'Rejected'].includes(record.approvalDecision);

  return (
    <div className='section-panel'>
      {/* Walkthrough banner */}
      {isWalkthrough && (
        <div className='alert alert--warn' style={{ marginBottom: 16 }}>
          <span className='alert__icon'>
            <i className='fa-solid fa-person-chalkboard' aria-hidden='true' />
          </span>
          <div className='alert__body'>
            <strong>Walkthrough Requested.</strong> The Approver (<strong>{approverDisplay}</strong>) has requested a walkthrough before approval. Contact them to arrange a session.
          </div>
        </div>
      )}

      <div className='section-panel__header'>
        <h2 className='section-panel__title'>
          <i className='fa-solid fa-stamp' aria-hidden='true' /> Step 5 —
          Approval
        </h2>
      </div>
      <div className='section-panel__body'>
        <div className='field-display-grid' style={{ marginBottom: 24 }}>
          <div className='field-display'>
            <div className='field-display__label'>Approver</div>
            <div className='field-display__value'>{approverDisplay}</div>
          </div>
          <div className='field-display'>
            <div className='field-display__label'>Status</div>
            <div className='field-display__value'>
              {hasDecision ? (
                <span
                  className={`badge badge--${record.approvalDecision === 'Approved' ? 'accent' : 'danger'}`}
                >
                  {record.approvalDecision}
                </span>
              ) : (
                <span className='badge badge--warn'>Pending Decision</span>
              )}
            </div>
          </div>
          {record.approvalDate && (
            <div className='field-display'>
              <div className='field-display__label'>Decision Date</div>
              <div className='field-display__value'>
                {fmtDate(record.approvalDate)}
              </div>
            </div>
          )}
          {record.rejectionReason && (
            <div className='field-display'>
              <div className='field-display__label'>Rejection Reason</div>
              <div className='field-display__value'>
                {record.rejectionReason}
              </div>
            </div>
          )}
        </div>

        {!hasDecision && !isWalkthrough && (
          <div
            style={{
              padding: '16px',
              background: 'var(--surface-muted)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: '0.875rem',
                color: 'var(--ink-dim)',
              }}
            >
              <i
                className='fa-solid fa-info-circle'
                aria-hidden='true'
                style={{ marginRight: 8 }}
              />
              Approval request sent to <strong>{approverDisplay}</strong>. Awaiting decision.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
